import { Button, SearchIcon } from '@calizahq/react-components-v2'
import { uniqBy } from 'lodash'
import compact from 'lodash/compact'
import pull from 'lodash/pull'
import React, { ChangeEvent, useEffect, useMemo, useState } from 'react'

import { Checkbox, Dropdown, SimpleInput } from '@/components'
import { FilterActions, useFiltersContext, useUserContext } from '@/context'
import { useMobile, useSearch } from '@/hooks'
import useMarketSegments from '@/hooks/useMarketSegments'
import { MarketSegment } from '@/types/MarketSegment'
import { normalize } from '@/util'

import { bySegmentName } from './MarketSegmentFilter.util'

type Props = {
  className?: string
}

export const MarketSegmentFilter = ({ className }: Props) => {
  const { user } = useUserContext()
  const [{ marketSegmentFilter }, FilterDispatch] = useFiltersContext()
  const savedFilterIds = marketSegmentFilter.marketSegmentIds ?? []
  const savedFilterHubs = marketSegmentFilter.fieldOperationsHubs ?? []
  const savedFilterRegions = marketSegmentFilter.fieldOperationsRegions ?? []
  const savedFilterTypes = marketSegmentFilter.marketSegmentTypes ?? []
  const [dropdownExpanded, setDropdownExpanded] = useState(false)
  const [selectedMarketSegmentIds, setSelectedMarketSegmentIds] = useState(savedFilterIds)
  const [selectedFieldOperationsHubs, setSelectedFieldOperationsHubs] = useState<string[]>(savedFilterHubs)
  const [selectedFieldOperationsRegions, setSelectedFieldOperationsRegions] = useState<string[]>(savedFilterRegions)
  const [selectedMarketSegmentTypes, setSelectedMarketSegmentTypes] = useState<string[]>(savedFilterTypes)
  const [searchQuery, setSearchQuery] = useState('')
  const { isMobile } = useMobile()

  const marketSegmentsQuery = useMarketSegments()

  useEffect(() => {
    const data = marketSegmentsQuery.data
    if (data && user?.marketSegmentIds === null && marketSegmentFilter.marketSegmentIds === null) {
      const defaultMarketSegmentId = data.marketSegments[0]?.id || 1
      FilterDispatch(
        FilterActions.setMarketSegments({
          ids: [defaultMarketSegmentId],
          marketSegmentTypes: [],
          fieldOperationsHubs: [],
          fieldOperationsRegions: [],
        }),
      )
      setSelectedMarketSegmentIds([defaultMarketSegmentId])
    }
    // Only run when data first arrives
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketSegmentsQuery.data])

  const { searchResults } = useSearch({
    data: marketSegmentsQuery?.data?.marketSegments ?? [],
    keysToSearch: ['name'],
    query: searchQuery,
  })

  const allMarketSegments = normalize(marketSegmentsQuery?.data?.marketSegments ?? [])

  const fieldOperationsHubs = useMemo(() => {
    const marketSegments = marketSegmentsQuery?.data?.marketSegments.filter((x) => x.fieldOperationsHub !== null)
    const filteredData = uniqBy(marketSegments, 'fieldOperationsHub')
    return filteredData.map((item) => item.fieldOperationsHub)
  }, [marketSegmentsQuery?.data?.marketSegments])

  const handleClickFieldOperationHub = (fieldOperationsHub) => {
    if (selectedFieldOperationsHubs.includes(fieldOperationsHub))
      setSelectedFieldOperationsHubs(pull([...selectedFieldOperationsHubs], fieldOperationsHub))
    else setSelectedFieldOperationsHubs([...selectedFieldOperationsHubs, fieldOperationsHub])
  }

  useEffect(() => {
    if (selectedFieldOperationsHubs.length > 0) {
      const marketSegments = marketSegmentsQuery?.data?.marketSegments.filter(
        (x) => x.fieldOperationsHub && selectedFieldOperationsHubs.includes(x.fieldOperationsHub),
      )
      const filteredMarketSegments = marketSegments?.map((x) => x.id) || [marketSegmentsQuery.data?.[0]?.id] || [1]
      FilterDispatch(
        FilterActions.setMarketSegments({
          ids: [...filteredMarketSegments],
          marketSegmentTypes: [],
          fieldOperationsHubs: [...selectedFieldOperationsHubs],
          fieldOperationsRegions: [],
        }),
      )
      setSelectedFieldOperationsRegions([])
      setSelectedMarketSegmentTypes([])
      setSelectedMarketSegmentIds(filteredMarketSegments)
    }
  }, [FilterDispatch, marketSegmentsQuery.data, selectedFieldOperationsHubs])

  const fieldOperationsRegions = useMemo(() => {
    const marketSegments = marketSegmentsQuery?.data?.marketSegments.filter((x) => x.fieldOperationsRegion !== null)
    const filteredData = uniqBy(marketSegments, 'fieldOperationsRegion')
    return filteredData.map((item) => item.fieldOperationsRegion)
  }, [marketSegmentsQuery?.data?.marketSegments])

  const handleClickFieldOperationRegion = (fieldOperationsRegion) => {
    if (selectedFieldOperationsRegions.includes(fieldOperationsRegion))
      setSelectedFieldOperationsRegions(pull([...selectedFieldOperationsRegions], fieldOperationsRegion))
    else setSelectedFieldOperationsRegions([...selectedFieldOperationsRegions, fieldOperationsRegion])
  }

  useEffect(() => {
    if (selectedFieldOperationsRegions.length > 0) {
      const marketSegments = marketSegmentsQuery?.data?.marketSegments.filter(
        (x) => x.fieldOperationsRegion && selectedFieldOperationsRegions.includes(x.fieldOperationsRegion),
      )
      const filteredMarketSegments = marketSegments?.map((x) => x.id) || [marketSegmentsQuery.data?.[0]?.id] || [1]
      FilterDispatch(
        FilterActions.setMarketSegments({
          ids: [...filteredMarketSegments],
          marketSegmentTypes: [],
          fieldOperationsHubs: [],
          fieldOperationsRegions: [...selectedFieldOperationsRegions],
        }),
      )
      setSelectedFieldOperationsHubs([])
      setSelectedMarketSegmentTypes([])
      setSelectedMarketSegmentIds(filteredMarketSegments)
    }
  }, [FilterDispatch, marketSegmentsQuery.data, selectedFieldOperationsRegions])

  const marketSegmentTypes = useMemo(() => {
    const marketSegments = marketSegmentsQuery?.data?.marketSegments.filter((x) => x.marketSegmentType !== null)
    const filteredData = uniqBy(marketSegments, 'marketSegmentType')
    return filteredData.map((item) => item.marketSegmentType)
  }, [marketSegmentsQuery?.data?.marketSegments])

  const handleClickMarketSegmentType = (marketSegmentType) => {
    if (selectedMarketSegmentTypes.includes(marketSegmentType))
      setSelectedMarketSegmentTypes(pull([...selectedMarketSegmentTypes], marketSegmentType))
    else setSelectedMarketSegmentTypes([...selectedMarketSegmentTypes, marketSegmentType])
  }

  useEffect(() => {
    if (selectedMarketSegmentTypes.length > 0) {
      const marketSegments = marketSegmentsQuery?.data?.marketSegments.filter(
        (x) => x.marketSegmentType && selectedMarketSegmentTypes.includes(x.marketSegmentType),
      )
      const filteredMarketSegments = marketSegments?.map((x) => x.id) || [marketSegmentsQuery.data?.[0]?.id] || [1]
      FilterDispatch(
        FilterActions.setMarketSegments({
          ids: [...filteredMarketSegments],
          marketSegmentTypes: [...selectedMarketSegmentTypes],
          fieldOperationsHubs: [],
          fieldOperationsRegions: [],
        }),
      )
      setSelectedFieldOperationsHubs([])
      setSelectedFieldOperationsRegions([])
      setSelectedMarketSegmentIds(filteredMarketSegments)
    }
  }, [FilterDispatch, marketSegmentsQuery.data, selectedMarketSegmentTypes])

  const savedFilterSelections = savedFilterIds.map((id) => allMarketSegments[id]).sort(bySegmentName)

  const unselectedSegments = Object.values(allMarketSegments)
    .filter((segment) => !savedFilterIds.includes(segment.id))
    .sort(bySegmentName)

  const displayedMarkets = !!searchQuery
    ? searchResults
    : compact(savedFilterSelections).concat(compact(unselectedSegments))

  const handleClick = (id: MarketSegment['id']) => {
    if (selectedMarketSegmentIds.includes(id)) setSelectedMarketSegmentIds(pull([...selectedMarketSegmentIds], id))
    else setSelectedMarketSegmentIds([...selectedMarketSegmentIds, id])
  }

  const handleApply = () => {
    FilterDispatch(
      FilterActions.setMarketSegments({
        ids: [...selectedMarketSegmentIds],
        marketSegmentTypes: [...selectedMarketSegmentTypes],
        fieldOperationsHubs: [...selectedFieldOperationsHubs],
        fieldOperationsRegions: [...selectedFieldOperationsRegions],
      }),
    )
    setDropdownExpanded(false)
  }

  const handleResetFilters = () => {
    setDropdownExpanded(false)
    setSearchQuery('')
    const resetMarketSegmentId = user?.marketSegmentIds || [marketSegmentsQuery.data?.[0]?.id] || [1]
    FilterDispatch(
      FilterActions.setMarketSegments({
        ids: [...resetMarketSegmentId],
        marketSegmentTypes: [],
        fieldOperationsHubs: [],
        fieldOperationsRegions: [],
      }),
    )
    setSelectedFieldOperationsHubs([])
    setSelectedFieldOperationsRegions([])
    setSelectedMarketSegmentTypes([])
    setSelectedMarketSegmentIds(resetMarketSegmentId)
  }

  const handleCancel = () => {
    setDropdownExpanded(false)
    setSelectedMarketSegmentIds(marketSegmentFilter.marketSegmentIds ?? [])
    setSearchQuery('')
  }

  const handleSearchInput = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }

  const renderButtons = () => {
    return (
      <div className="flex flex-row items-center justify-between px-6 pb-4 pt-4">
        {isMobile ? (
          <Button
            className="mr-4 flex-1 bg-grey-100"
            onClick={handleResetFilters}
            size={isMobile ? undefined : 'small'}
            variant={'secondary'}
          >
            Reset
          </Button>
        ) : (
          <Button onClick={handleResetFilters} size={isMobile ? undefined : 'small'} variant={'secondary'}>
            <span className="underline underline-offset-2">Reset</span>
          </Button>
        )}

        <Button
          className="flex-1 bg-text-semantic-success md:flex-0"
          onClick={handleApply}
          size={isMobile ? undefined : 'small'}
        >
          Apply
        </Button>
      </div>
    )
  }

  const renderHeader = () => {
    return (
      <div className="md:px-6 md:pt-6">
        <SimpleInput
          containerClassName="mt-2 mb-6"
          onChange={handleSearchInput}
          placeholder="Search market segments"
          renderLeftIcon={SearchIcon}
          value={searchQuery}
        />
      </div>
    )
  }

  return (
    <Dropdown
      className={className}
      displayedCount={marketSegmentFilter.marketSegmentIds?.length ?? 0}
      expanded={dropdownExpanded}
      fullScreenMobile={false}
      headerTitle="Market Segment"
      onHideDropdown={handleCancel}
      renderFooter={renderButtons}
      renderHeader={renderHeader}
      selected={!!savedFilterIds.length}
      setExpanded={setDropdownExpanded}
    >
      <div className="md:w-120">
        {!!searchQuery && searchResults.length === 0 ? (
          <p className="w-full px-6 text-center italic text-tertiary">{`No matches for "${searchQuery}"`}</p>
        ) : null}

        {fieldOperationsHubs?.length > 0 ? (
          <div className="mb-4 border-b border-secondary">
            <div className="flex flex-row px-6 mb-2 font-bold text-header-sm">Field Operations Hubs</div>
            <div
              className={`grid max-h-72 grid-cols-${isMobile ? '2' : '3'} gap-x-14 gap-y-7 px-6 pb-6  md:overflow-auto`}
            >
              {fieldOperationsHubs.map((fieldOperationsHub) => (
                <div
                  className="flex cursor-pointer flex-row items-center"
                  key={fieldOperationsHub}
                  onClick={() => handleClickFieldOperationHub(fieldOperationsHub)}
                  role="presentation"
                >
                  <Checkbox
                    checked={selectedFieldOperationsHubs.includes(fieldOperationsHub || '')}
                    onClick={() => handleClickFieldOperationHub(fieldOperationsHub)}
                  />
                  <div className="text-sm">{fieldOperationsHub}</div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {fieldOperationsRegions?.length > 0 ? (
          <div className="mb-4 border-b border-secondary">
            <div className="flex flex-row px-6 mb-2 font-bold text-header-sm">Field Operations Regions</div>
            <div
              className={`grid max-h-72 grid-cols-${isMobile ? '2' : '3'} gap-x-14 gap-y-7 px-6 pb-6  md:overflow-auto`}
            >
              {fieldOperationsRegions.map((fieldOperationsRegion) => (
                <div
                  className="flex cursor-pointer flex-row items-center"
                  key={fieldOperationsRegion}
                  onClick={() => handleClickFieldOperationRegion(fieldOperationsRegion)}
                  role="presentation"
                >
                  <Checkbox
                    checked={selectedFieldOperationsRegions.includes(fieldOperationsRegion || '')}
                    onClick={() => handleClickFieldOperationRegion(fieldOperationsRegion)}
                  />
                  <div className="text-sm">{fieldOperationsRegion}</div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {marketSegmentTypes?.length > 0 ? (
          <div className="mb-4 border-b border-secondary">
            <div className="flex flex-row px-6 mb-2 font-bold text-header-sm">Market Segment Types</div>
            <div
              className={`grid max-h-72 grid-cols-${isMobile ? '2' : '3'} gap-x-14 gap-y-7 px-6 pb-6  md:overflow-auto`}
            >
              {marketSegmentTypes.map((marketSegmentType) => (
                <div
                  className="flex cursor-pointer flex-row items-center"
                  key={marketSegmentType}
                  onClick={() => handleClickMarketSegmentType(marketSegmentType)}
                  role="presentation"
                >
                  <Checkbox
                    checked={selectedMarketSegmentTypes.includes(marketSegmentType || '')}
                    onClick={() => handleClickMarketSegmentType(marketSegmentType)}
                  />
                  <div className="text-sm">{marketSegmentType}</div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="flex flex-row px-6 mb-2 font-bold text-header-sm">Market Segments</div>
        <div className={`grid max-h-72 grid-cols-${isMobile ? '2' : '3'} gap-x-14 gap-y-7 px-6 pb-6 md:overflow-auto`}>
          {displayedMarkets.map((segment) => (
            <div
              className="flex cursor-pointer flex-row items-center"
              key={segment.id}
              onClick={() => handleClick(segment.id)}
              role="presentation"
            >
              <Checkbox
                checked={selectedMarketSegmentIds.includes(segment.id)}
                onClick={() => handleClick(segment.id)}
              />
              <div className="text-sm">{segment.name}</div>
            </div>
          ))}
          <div className="h-8 col-span-2 md:hidden" />
        </div>
      </div>
    </Dropdown>
  )
}
